import { useCallback, useMemo, useState } from 'react'

import {
  OnFieldMappingChange,
  OnFieldMappingReplace
} from '../types/FieldsMappingTypes'
import { useRequiredField } from '../../hooks/useRequiredField'
import {
  UsersImportMappingFragment,
  UsersCsvImportModelFragment
} from '../../operations/UsersImportFragments.codegen'

interface IMapping {
  id?: number
  map: string[]
}

export interface ICurrentMappingData {
  currentMapping: IMapping
  onChange: OnFieldMappingChange
  onReplace: OnFieldMappingReplace
  requiredField: 'email' | 'account_name'
  requiredFieldMapped: boolean
}

const mappingFragmentToMapping = (
  mappingFragment: UsersImportMappingFragment
): IMapping => {
  const { id, map } = mappingFragment

  return { id, map }
}

const createCustomMapping = (map: string[]): IMapping => ({
  map,
  id: undefined
})

const mappingFor = (usersImport?: UsersCsvImportModelFragment): IMapping => {
  if (!usersImport) {
    return { map: [] }
  }

  if (usersImport.importMapping) {
    return mappingFragmentToMapping(usersImport.importMapping)
  }

  const emptyMap = usersImport.configuration.samples.map(() => '')
  return createCustomMapping(emptyMap)
}

export const useCurrentMapping = (
  usersImport: UsersCsvImportModelFragment
): ICurrentMappingData => {
  const [currentMapping, setCurrentMapping] = useState<IMapping>(
    mappingFor(usersImport)
  )

  const onReplace: OnFieldMappingReplace = useCallback(
    (newMapping) => {
      if (!newMapping) {
        setCurrentMapping({
          id: undefined,
          map: currentMapping.map
        })
        return
      }

      setCurrentMapping(mappingFragmentToMapping(newMapping))
    },
    [setCurrentMapping, currentMapping]
  )

  const onChange: OnFieldMappingChange = useCallback(
    (mappedIdx, field) => {
      const newMap = [...currentMapping.map]

      if (field) {
        const currentIdx = newMap.findIndex((val) => val === field)
        if (currentIdx) {
          newMap[currentIdx] = ''
        }
      }

      newMap[mappedIdx] = field || ''

      setCurrentMapping(createCustomMapping(newMap))
    },
    [setCurrentMapping, currentMapping]
  )

  const requiredField = useRequiredField()

  const requiredFieldMapped = useMemo(
    () => currentMapping.map.includes(requiredField),
    [requiredField, currentMapping]
  )

  return {
    currentMapping,
    onChange,
    onReplace,
    requiredField,
    requiredFieldMapped
  }
}
